import { useCallback, useEffect, useRef, useState } from "react";

import api, { unwrapList } from "utils/api";
import { API_ENDPOINTS } from "config/api";
import { extractErrorMessage } from "utils/apiError";
import { toast } from "utils/toast";
import { filenameForUpload, findPersistedUpload, isUploadCancelledError, transcriptionFromUpload } from "utils/upload";
import { useDirectUpload } from "hooks/use-direct-upload";
import { translations } from "utils/i18n";
import { findProjectName, transcriptName } from "utils/transcript";
import { useLanguage } from "context/languageContext";
import { Skeleton } from "components/ui/skeleton";
import { IN_FLIGHT_STATUSES, TranscriptionList, TranscriptionListSkeleton } from "components/transcription-list";
import { NameDialog } from "components/name-dialog";
import { UploadDialog } from "components/upload-dialog";
import { RecordDialog } from "components/record-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import { ActionTiles } from "components/action-tiles";

const CAPTION_FORMATS = ["MP4", "MOV", "MKV", "WEBM"];

export default function TranscriptionPage() {
  const { language } = useLanguage();
  const trList = translations[language].transcriptionList;
  const trUpload = translations[language].uploadFlow;
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const upload = useDirectUpload({ scope: "home" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [activeDialog, setActiveDialog] = useState(null); // null | "upload" | "record" | "caption"

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get(API_ENDPOINTS.TRANSCRIPTION.HISTORY), api.get(API_ENDPOINTS.PROJECT.LIST)])
      .then(([historyRes, projectRes]) => {
        if (cancelled) return;
        setItems(unwrapList(historyRes.data));
        setProjects(unwrapList(projectRes.data));
      })
      .catch((err) => {
        if (!cancelled) toast.error(extractErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshHistory = useCallback(async () => {
    const { data } = await api.get(API_ENDPOINTS.TRANSCRIPTION.HISTORY);
    setItems(unwrapList(data));
  }, []);

  const applyCompletedUpload = useCallback(
    (completedUpload, meta) => {
      const transcript = transcriptionFromUpload(completedUpload, meta);
      if (!transcript) throw new Error(trUpload.missingIdError);
      setItems((current) => {
        const exists = current.some((item) => item.id === transcript.id);
        return exists
          ? current.map((item) => (item.id === transcript.id ? { ...item, ...transcript } : item))
          : [transcript, ...current];
      });
    },
    [trUpload]
  );

  // Reattach to whatever upload was in flight before this page last
  // unmounted (a refresh, or navigating away and back) — see
  // useDirectUpload's `scope` option. Runs once per mount; the ref guards
  // against React StrictMode's dev-only double-invoke calling resume() twice.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;
    const record = findPersistedUpload("home");
    if (!record) return;
    upload
      .resume(record)
      .then((completedUpload) => {
        applyCompletedUpload(completedUpload, record);
        toast.success(trUpload.uploadComplete);
      })
      .catch((err) => {
        if (!isUploadCancelledError(err)) toast.error(extractErrorMessage(err));
      })
      .finally(() => setActiveDialog(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollRef = useRef(null);
  useEffect(() => {
    const hasInFlight = items.some((t) => IN_FLIGHT_STATUSES.includes(t.status));
    if (hasInFlight && !pollRef.current) {
      pollRef.current = setInterval(() => refreshHistory().catch(() => {}), 3000);
    }
    if (!hasInFlight && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [items, refreshHistory]);

  async function handleDelete(id) {
    try {
      await api.delete(API_ENDPOINTS.TRANSCRIPTION.DETAIL(id));
      setItems((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handleRename(id, name) {
    try {
      const { data } = await api.patch(API_ENDPOINTS.TRANSCRIPTION.DETAIL(id), { name });
      setItems((prev) => prev.map((t) => (t.id === id ? { ...t, name: data.name ?? name } : t)));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handleMoveToProject(item, projectId) {
    try {
      const { data } = await api.patch(API_ENDPOINTS.TRANSCRIPTION.DETAIL(item.id), { project: projectId });
      setItems((prev) => prev.map((t) => (t.id === item.id ? { ...t, project: data.project } : t)));
      toast.success(
        projectId ? trList.movedToProject(findProjectName(projects, projectId)) : trList.removedFromProject
      );
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handleUpload(fileOrBlob, source, customName) {
    try {
      const filename = filenameForUpload(fileOrBlob, source === "recorded" ? customName : undefined);
      const name = customName || filename;
      const transcriptLanguage = "ar";
      const meta = { filename, language: transcriptLanguage, source, name, project: null };
      const completedUpload = await upload.start(fileOrBlob, meta);
      applyCompletedUpload(completedUpload, meta);
      toast.success(trUpload.uploadComplete);
    } catch (err) {
      if (isUploadCancelledError(err)) toast.info(trUpload.uploadCancelled);
      else toast.error(extractErrorMessage(err));
      throw err;
    }
  }

  const uploading = upload.busy;
  const visibleFileCount = items.length;

  useEffect(() => {
    if (!upload.busy || activeDialog) return;
    if (upload.source === "recorded") setActiveDialog("record");
    else if (upload.source === "caption") setActiveDialog("caption");
    else setActiveDialog("upload");
  }, [activeDialog, upload.busy, upload.source]);

  const uploadDialogs = (
    <>
      <UploadDialog
        open={activeDialog === "upload"}
        onOpenChange={(open) => setActiveDialog(open ? "upload" : null)}
        onUpload={handleUpload}
        uploading={uploading}
        upload={upload}
      />
      <RecordDialog
        open={activeDialog === "record"}
        onOpenChange={(open) => setActiveDialog(open ? "record" : null)}
        onUpload={handleUpload}
        uploading={uploading}
        upload={upload}
      />
      <UploadDialog
        open={activeDialog === "caption"}
        onOpenChange={(open) => setActiveDialog(open ? "caption" : null)}
        onUpload={handleUpload}
        uploading={uploading}
        upload={upload}
        source="caption"
        title={trUpload.captionUploadTitle}
        prompt={trUpload.captionUploadPrompt}
        accept="video/*"
        formats={CAPTION_FORMATS}
      />
    </>
  );

  if (loading) {
    return (
      <div className="transcribe-page mx-auto w-full max-w-5xl px-6 py-8 md:px-10 md:py-10">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>

        <div className="pt-8">
          <Skeleton className="mb-3 h-4 w-16" />
          <TranscriptionListSkeleton />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="transcribe-page mx-auto w-full max-w-5xl px-6 py-8 md:px-10 md:py-10">
        <ActionTiles
          onUpload={() => setActiveDialog("upload")}
          onRecord={() => setActiveDialog("record")}
          onCaption={() => setActiveDialog("caption")}
          uploading={uploading}
        />

        <div className="pt-8">
          <p className="pb-3 text-sm text-muted-foreground">{trList.filesCount(visibleFileCount)}</p>
          <TranscriptionList
            items={items}
            projects={projects}
            onRename={setRenameTarget}
            onDelete={setDeleteTarget}
            onMoveToProject={handleMoveToProject}
          />
        </div>

        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{trList.deleteTranscriptionTitle}</AlertDialogTitle>
              <AlertDialogDescription>{trList.deleteTranscriptionDescription}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{trList.cancelLabel}</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (deleteTarget) handleDelete(deleteTarget.id);
                  setDeleteTarget(null);
                }}
              >
                {trList.delete}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <NameDialog
          open={!!renameTarget}
          onOpenChange={(open) => !open && setRenameTarget(null)}
          title={trList.renameTranscriptionTitle}
          label={trList.nameLabel}
          initialValue={renameTarget ? transcriptName(renameTarget, language) : ""}
          submitLabel={trList.saveLabel}
          onSubmit={(value) => {
            if (renameTarget) handleRename(renameTarget.id, value);
          }}
        />
      </div>
      {uploadDialogs}
    </>
  );
}
